import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, Lock, RefreshCw, LogOut, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { trackInitiateCheckout } from "@/utils/metaPixel";
import { toast } from "sonner";
import type { User } from "@supabase/supabase-js";

const POLAR_PRODUCT_ID = '6937b0e2-6510-4b4c-9470-f28f981cd131';
const SUPABASE_URL = "https://ancujowsxivyfnxhnsmr.supabase.co";

interface SubscriptionRequiredCardProps {
  user: User | null;
}

export const SubscriptionRequiredCard: React.FC<SubscriptionRequiredCardProps> = ({ user }) => {
  const [creatingCheckout, setCreatingCheckout] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showEmailReminder, setShowEmailReminder] = useState(false);
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate('/');
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Error signing out. Please try again.');
    }
  };

  const syncSubscription = async () => {
    if (!user) {
      toast.error('Please log in to continue');
      return;
    }

    // Prevent multiple simultaneous sync attempts
    if (syncing) {
      return;
    }

    try {
      setSyncing(true);

      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        console.error('Session error:', sessionError);
        toast.error('Please log in to continue');
        setSyncing(false);
        return;
      }

      
      const response = await fetch(`${SUPABASE_URL}/functions/v1/polar-sync-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (!response.ok) {
        let errorMessage = 'Failed to sync subscription';
        if (data.error) {
          if (typeof data.error === 'string') {
            errorMessage = data.error;
          } else if (data.error.message) {
            errorMessage = data.error.message;
          } else {
            errorMessage = JSON.stringify(data.error);
          }
        }
        throw new Error(errorMessage);
      }

      if (data.success) {
        toast.success('Subscription found! Refreshing page...');
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        throw new Error('Sync did not complete successfully');
      }
    } catch (error: any) {
      console.error('Sync error:', error);
      // Show email reminder when sync fails
      setShowEmailReminder(true);
      
      let errorMessage = 'No active subscription found. Please subscribe to continue.';
      if (error) {
        if (typeof error === 'string') {
          // Clean up error messages to not mention Polar
          const errorStr = error.toLowerCase();
          if (errorStr.includes('polar') || errorStr.includes('customer') || errorStr.includes('sync')) {
            errorMessage = 'No active subscription found. Please subscribe to continue.';
          } else {
            errorMessage = error;
          }
        } else if (error.message) {
          const errorMsg = typeof error.message === 'string' ? error.message : String(error.message);
          const errorMsgLower = errorMsg.toLowerCase();
          if (errorMsgLower.includes('polar') || errorMsgLower.includes('customer') || errorMsgLower.includes('sync')) {
            errorMessage = 'No active subscription found. Please subscribe to continue.';
          } else {
            errorMessage = errorMsg;
          }
        }
      }
      toast.error(errorMessage);
      setSyncing(false);
    }
  };

  const createCheckout = async () => {
    if (!user) {
      toast.error('Please log in to continue');
      return;
    }

    try {
      setCreatingCheckout(true);

      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        console.error('Session error:', sessionError);
        toast.error('Please log in to continue');
        setCreatingCheckout(false);
        return;
      }

      // Get user's country from profile if available
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('country')
        .eq('user_id', user.id)
        .single();

      // Profile error is not critical - we can proceed without country
      if (profileError && profileError.code !== 'PGRST116') {
        console.warn('Profile fetch error (non-critical):', profileError);
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/polar-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          product_id: POLAR_PRODUCT_ID,
          success_url: `${window.location.origin}/dashboard?subscription=success`,
          country: profile?.country || undefined,
        }),
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (!response.ok) {
        // Check if subscription was synced
        if (data.synced) {
          toast.success('Subscription found! Refreshing...');
          setTimeout(() => {
            window.location.reload();
          }, 1500);
          return;
        }
        
        // Extract error message - handle both string and object formats
        // Show actual error in development, sanitize in production
        const isDevelopment = import.meta.env.DEV || window.location.hostname === 'localhost';
        let errorMessage = 'Unable to start subscription process. Please try again.';
        
        if (data.error) {
          if (typeof data.error === 'string') {
            errorMessage = isDevelopment ? data.error : 'Unable to start subscription process. Please try again.';
          } else if (data.error.message) {
            errorMessage = isDevelopment ? data.error.message : 'Unable to start subscription process. Please try again.';
          } else if (data.error.detail) {
            const detailStr = typeof data.error.detail === 'string' ? data.error.detail : JSON.stringify(data.error.detail);
            errorMessage = isDevelopment ? detailStr : 'Unable to start subscription process. Please try again.';
          }
        } else if (data.message) {
          const msgStr = typeof data.message === 'string' ? data.message : JSON.stringify(data.message);
          errorMessage = isDevelopment ? msgStr : 'Unable to start subscription process. Please try again.';
        }
        
        console.error('Checkout API error:', errorMessage);
        throw new Error(errorMessage);
      }

      // Check if subscription was synced (successful sync response)
      if (data.synced) {
        toast.success('Subscription found! Refreshing...');
        setTimeout(() => {
          window.location.reload();
        }, 1500);
        return;
      }

      if (!data.checkout_url) {
        console.error('No checkout_url in response:', data);
        throw new Error('No checkout URL received from server');
      }

      // Track checkout initiation
      trackInitiateCheckout({
        content_name: 'Subscription Checkout',
        content_category: 'Subscription',
        value: 87.90, // Your subscription price
        currency: 'USD',
        num_items: 1
      });
      
      // Redirect to Polar checkout
      window.location.href = data.checkout_url;
      // Note: We don't reset creatingCheckout here because we're redirecting
    } catch (error: any) {
      console.error('Checkout error:', error);
      // Extract error message safely - handle both Error objects and plain objects
      // Clean up messages to not mention Polar
      let errorMessage = 'Unable to start subscription process. Please try again.';
      if (error) {
        if (typeof error === 'string') {
          const errorStr = error.toLowerCase();
          if (errorStr.includes('polar') || errorStr.includes('checkout') || errorStr.includes('customer')) {
            errorMessage = 'Unable to start subscription process. Please try again.';
          } else {
            errorMessage = error;
          }
        } else if (error.message) {
          const errorMsg = typeof error.message === 'string' ? error.message : String(error.message);
          const errorMsgLower = errorMsg.toLowerCase();
          if (errorMsgLower.includes('polar') || errorMsgLower.includes('checkout') || errorMsgLower.includes('customer')) {
            errorMessage = 'Unable to start subscription process. Please try again.';
          } else {
            errorMessage = errorMsg;
          }
        } else if (error.error) {
          const errorErr = typeof error.error === 'string' ? error.error : String(error.error);
          const errorErrLower = errorErr.toLowerCase();
          if (errorErrLower.includes('polar') || errorErrLower.includes('checkout') || errorErrLower.includes('customer')) {
            errorMessage = 'Unable to start subscription process. Please try again.';
          } else {
            errorMessage = errorErr;
          }
        }
      }
      toast.error(errorMessage);
      setCreatingCheckout(false);
    }
  };

  return (
    <Card 
      className="
        bg-gradient-to-br from-primary/10 via-primary/5 to-background
        border-primary/20
        shadow-[0_8px_30px_rgb(0,0,0,0.08)]
        rounded-3xl
        overflow-hidden
        relative
        group
        hover:shadow-[0_12px_40px_rgba(59,130,246,0.15)]
        transition-all duration-500
        z-[60]
        pointer-events-auto
      "
      style={{ pointerEvents: 'auto', zIndex: 60 }}
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none z-0" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-400/5 rounded-full blur-3xl translate-y-1/3 -translate-x-1/3 pointer-events-none z-0" />

      <CardHeader className="relative z-10 text-center pb-4 pt-8 px-8">
        <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/15 border border-primary/20 mb-4 mx-auto">
          <Lock className="h-8 w-8 text-primary" />
        </div>
        <CardTitle className="text-2xl font-bold text-foreground tracking-tight mb-2">
          Subscription Required
        </CardTitle>
        <CardDescription className="text-base text-muted-foreground max-w-md mx-auto">
          Start your subscription to continue.
        </CardDescription>
      </CardHeader>

      <CardContent className="relative z-10 px-8 pb-8 pointer-events-auto">
        <div className="flex flex-col items-center gap-4 pointer-events-auto">
          <Button
            className="w-full rounded-full bg-gradient-to-r from-primary via-primary to-primary/90 px-8 py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3 min-h-[60px] group relative overflow-hidden"
            onClick={createCheckout}
            disabled={creatingCheckout || syncing}
            type="button"
          >
            {creatingCheckout ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                <span>Loading...</span>
              </>
            ) : (
              <>
                <span className="relative z-10">Subscribe</span>
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform relative z-10" />
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 transform translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500" />
              </>
            )}
          </Button>
              <div className="w-full text-center">
                <button
                  onClick={syncSubscription}
                  disabled={syncing || creatingCheckout}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5 group"
                  type="button"
                >
                  {syncing ? (
                    <>
                      <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                      <span>Checking...</span>
                    </>
                  ) : (
                    <>
                      <span className="group-hover:underline">Already subscribed?</span>
                    </>
                  )}
                </button>
              </div>
        </div>
        
        {/* Email reminder - slides in smoothly below without changing card size */}
        <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
          showEmailReminder ? 'max-h-96 opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0'
        }`}>
          {showEmailReminder && user?.email && (
            <div className="pt-4 border-t border-border/30">
              <div className="p-4 rounded-2xl bg-muted/50 border border-border/30">
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground mb-1">
                      Logged in as: <span className="font-semibold">{user.email}</span>
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                      Make sure you're logged in with the same email address you used when subscribing. If you subscribed with a different email, please log out and sign in with the correct account.
                    </p>
                    <Button
                      variant="ghost"
                      onClick={handleSignOut}
                      className="w-full text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors duration-200 flex items-center justify-center gap-2"
                      type="button"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>Log out</span>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

